/**
 * Appointments — three tabs:
 *   Next      the single soonest upcoming session, highlighted, with manage actions
 *   Calendar  a month grid of all scheduled sessions (<SessionsCalendar/>)
 *   Book      the day-first booking picker (<BookingPicker/>)
 *
 * Cancel/reschedule are shared across Next + Calendar. Recurring sessions show a
 * badge and cancel offers "this one" vs "all upcoming". Coach name resolved
 * server-side.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Appointment } from '@regis/shared';
import { api } from '@/lib/api';
import { Button, Card, Muted, Pill } from '@/components/ui';
import { PageHeader } from '@/components/PageHeader';
import { BookingPicker } from './BookingPicker';
import { SessionsCalendar } from './SessionsCalendar';
import { RescheduleModal } from './RescheduleModal';

function fmtDateLong(d: string): string {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}
function label12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

type Tab = 'next' | 'calendar' | 'book';

export default function AppointmentsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('next');
  const [rescheduling, setRescheduling] = useState<Appointment | null>(null);

  const appts = useQuery({
    queryKey: ['appointments'],
    queryFn: () => api.get<{ appointments: Appointment[] }>('/api/appointments'),
  });

  const cancel = useMutation({
    mutationFn: ({ id, scope }: { id: string; scope: 'one' | 'series' }) =>
      api.post(`/api/appointments/${id}/cancel?scope=${scope}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['slots'] });
      qc.invalidateQueries({ queryKey: ['booking-cost'] });
    },
  });

  // A recurring booking offers "this one" vs "all upcoming"; a standalone just cancels.
  function onCancel(a: Appointment) {
    if (a.series_id) {
      const all = window.confirm(
        'This is a recurring session.\n\nOK = cancel THIS and all future sessions in the series.\nCancel = cancel only this one.'
      );
      cancel.mutate({ id: a.id, scope: all ? 'series' : 'one' });
    } else {
      cancel.mutate({ id: a.id, scope: 'one' });
    }
  }

  // Upcoming sessions, soonest first.
  const upcoming = (appts.data?.appointments ?? [])
    .filter((a) => a.status === 'upcoming')
    .sort((x, y) => (x.date === y.date ? x.start_time.localeCompare(y.start_time) : x.date.localeCompare(y.date)));
  const next = upcoming[0];

  return (
    <>
      <PageHeader title="Appointments" subtitle="Sessions & booking" />
      <div className="px-4" style={{ display: 'grid', gap: '1.25rem', minWidth: 0 }}>
        {/* Tab bar */}
        <div style={{ display: 'inline-flex', borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--card-border)', width: '100%' }}>
          {([['next', 'Next'], ['calendar', 'Calendar'], ['book', 'Book']] as Array<[Tab, string]>).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              aria-pressed={tab === key}
              style={{
                flex: 1,
                padding: '0.55rem 0.5rem',
                fontSize: '0.85rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: tab === key ? 'var(--gold)' : 'transparent',
                color: tab === key ? '#1a1a1a' : 'var(--text-tertiary)',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* NEXT */}
        {tab === 'next' && (
          <section style={{ minWidth: 0 }}>
            {appts.isLoading ? (
              <Muted>Loading…</Muted>
            ) : !next ? (
              <Card>
                <Muted>No upcoming sessions yet.</Muted>
                <div style={{ marginTop: '0.75rem' }}>
                  <Button onClick={() => setTab('book')}>Book a session</Button>
                </div>
              </Card>
            ) : (
              <Card style={{ border: '1px solid var(--gold)', background: 'linear-gradient(135deg, rgba(201,168,106,0.10), var(--card-bg))' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--gold-muted)', marginBottom: '0.35rem' }}>
                  Your next session
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: '0.75rem' }}>
                  <div style={{ flex: '1 1 12rem', minWidth: 0 }}>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      {fmtDateLong(next.date)}
                      {next.series_id ? (
                        <span style={{ fontSize: '0.62rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--gold)', border: '1px solid var(--gold-muted)', borderRadius: 999, padding: '0.05rem 0.4rem' }}>↻ Recurring</span>
                      ) : null}
                    </div>
                    <Muted>
                      {label12(next.start_time)} – {label12(next.end_time)}
                      {next.appointment_type ? ` · ${next.appointment_type}` : ''}
                      {next.trainer ? ` · ${next.trainer.first_name ?? ''} ${next.trainer.last_name ?? ''}` : ''}
                    </Muted>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                    <Button variant="ghost" onClick={() => setRescheduling(next)}>Reschedule</Button>
                    <Button variant="ghost" onClick={() => onCancel(next)} disabled={cancel.isPending}>Cancel</Button>
                  </div>
                </div>
                {upcoming.length > 1 && (
                  <div style={{ marginTop: '0.85rem' }}>
                    <Button variant="ghost" onClick={() => setTab('calendar')}>
                      View all {upcoming.length} sessions →
                    </Button>
                  </div>
                )}
              </Card>
            )}
          </section>
        )}

        {/* CALENDAR */}
        {tab === 'calendar' && (
          <section style={{ minWidth: 0 }}>
            {appts.isLoading ? (
              <Muted>Loading…</Muted>
            ) : (
              <SessionsCalendar
                appointments={appts.data?.appointments ?? []}
                onReschedule={(a) => setRescheduling(a)}
                onCancel={onCancel}
                cancelPending={cancel.isPending}
              />
            )}
          </section>
        )}

        {/* BOOK */}
        {tab === 'book' && (
          <section style={{ minWidth: 0 }}>
            <BookingPicker />
          </section>
        )}

        {cancel.isError && <Pill tone="error">Action failed — please retry.</Pill>}
      </div>

      {rescheduling && (
        <RescheduleModal appt={rescheduling} onClose={() => setRescheduling(null)} />
      )}
    </>
  );
}
